'use client'

import { type UseQueryResult, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { client } from '@/lib/api'
import { YYYY_MM_DD_HH_MM } from '@/lib/constants/dateFormat'
import type { ApiResponse, TenantMaintenanceHistoriesTableData, UseQueryOptions } from '@/types'
import type { paths } from '@/types/schema'

export const GET_TENANT_MAINTENANCE_HISTORIES = '/tenants/maintenance/histories'

const defaultLimit = 100

export type UseFetchTenantMaintenanceHistories = UseQueryResult<
  ApiResponse<TenantMaintenanceHistoriesTableData[]>,
  Error
>

// テナントメンテナンス履歴取得
export function useFetchTenantMaintenanceHistories({
  params,
  reactQuery,
}: UseQueryOptions<
  paths[typeof GET_TENANT_MAINTENANCE_HISTORIES]['get']
>): UseFetchTenantMaintenanceHistories {
  return useQuery({
    ...reactQuery,
    queryKey: [GET_TENANT_MAINTENANCE_HISTORIES, params?.query?.offset, params?.query?.limit],
    queryFn: async ({ signal }) => {
      const {
        data,
        error,
        response: { status },
      } = await client.GET(GET_TENANT_MAINTENANCE_HISTORIES, {
        params: {
          query: {
            offset: params?.query?.offset ?? 0,
            limit: params?.query?.limit ?? defaultLimit,
          },
        },
        signal,
      })

      return {
        apiData: data,
        apiError: error,
        apiStatus: status,
      }
    },
    // データ変換はselectで分離
    select(result) {
      const { apiData, apiError, apiStatus } = result
      // ApiResponse<MaintenanceHistory[]> 型のオブジェクトを返す
      return {
        apiData: apiData ? convertMaintenanceHistoriesToTableData(apiData.list) : [],
        apiError,
        apiStatus,
      }
    },
  })
}

// テーブルデータ変換
export function convertMaintenanceHistoriesToTableData(
  histories: unknown[]
): TenantMaintenanceHistoriesTableData[] {
  return (
    histories?.map((history: unknown) => {
      const h = history as Record<string, unknown>
      return {
        executedUser: (h.executedUserName as string) || '-',
        executedAt: h.executedAt ? dayjs(h.executedAt as string).format(YYYY_MM_DD_HH_MM) : '-',
        actionType: h.actionType === 'enter' ? 'enter' : 'exit',
      }
    }) || []
  )
}
